import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkin } from 'src/api/types/checkin-types';
import {
  FetchCheckinsPageOptions,
  fetchCheckinsPage,
} from 'src/api/services/checkins-service';
import { useAppAuth } from 'src/context/use-app-auth';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

type RangeKey = '7d' | '30d' | 'all';
const pageSize = 30;

type CheckinsContextValue = {
  loading: boolean;
  loadingPage: boolean;
  hasMore: boolean;
  range: RangeKey;
  // eslint-disable-next-line no-unused-vars
  setRange: (value: RangeKey) => void;
  optInOnly: boolean;
  // eslint-disable-next-line no-unused-vars
  setOptInOnly: (value: boolean) => void;
  search: string;
  // eslint-disable-next-line no-unused-vars
  setSearch: (value: string) => void;
  currentPage: number;
  totalPages: number;
  visibleCheckins: Checkin[];
  filteredCheckins: Checkin[];
  recipientsCount: number;
  stats: { total: number; optedIn: number; recent: number };
  handleNextPage: () => Promise<void>;
  handlePrevPage: () => void;
  refresh: () => Promise<void>;
};

const CheckinsContext = createContext<CheckinsContextValue | undefined>(
  undefined,
);

export const useCheckins = () => {
  const ctx = useContext(CheckinsContext);
  if (!ctx) {
    throw new Error('useCheckins must be used within CheckinsProvider');
  }
  return ctx;
};

export const CheckinsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { location } = useAppAuth();

  const [pages, setPages] = useState<Checkin[][]>([]);
  const pagesRef = useRef<Checkin[][]>([]);
  const cursorsRef = useRef<(QueryDocumentSnapshot<DocumentData> | null)[]>([]);
  const inFlightRef = useRef(false);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingPage, setLoadingPage] = useState(false);
  const [range, setRange] = useState<RangeKey>('all');
  const [optInOnly, setOptInOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const loadPage = useCallback(
    async (targetPage: number, opts: FetchCheckinsPageOptions = {}) => {
      if (!location?.key || pagesRef.current[targetPage] || inFlightRef.current)
        return;
      inFlightRef.current = true;
      setLoading(true);
      setLoadingPage(true);
      try {
        const cursor =
          targetPage > 0 ? cursorsRef.current[targetPage - 1] : undefined;
        const result = await fetchCheckinsPage(location.key, {
          limit: pageSize,
          cursor,
          optInOnly,
          ...opts,
        });

        setPages(prev => {
          const next = [...prev];
          next[targetPage] = result.items;
          pagesRef.current = next;
          return next;
        });
        cursorsRef.current = (() => {
          const next = [...cursorsRef.current];
          next[targetPage] = result.cursor;
          return next;
        })();
        setHasMore(result.hasMore);
      } finally {
        setLoading(false);
        setLoadingPage(false);
        inFlightRef.current = false;
      }
    },
    [location?.key, optInOnly],
  );

  const refresh = useCallback(async () => {
    setPages([]);
    pagesRef.current = [];
    cursorsRef.current = [];
    setPage(0);
    setHasMore(false);
    if (location?.key) {
      await loadPage(0, { cursor: null });
    }
  }, [location?.key, loadPage]);

  useEffect(() => {
    refresh();
  }, [refresh, optInOnly, range, location?.key]);

  const combinedCheckins = useMemo(() => pages.flat(), [pages]);

  const filteredCheckins = useMemo(() => {
    const now = Date.now();
    const q = search.trim().toLowerCase();
    const daysBack = range === '7d' ? 7 : range === '30d' ? 30 : 0;
    const earliest =
      daysBack > 0 ? now - daysBack * 24 * 60 * 60 * 1000 : undefined;
    return combinedCheckins.filter(entry => {
      if (optInOnly && !entry.optIn) return false;

      if (earliest) {
        if (!entry.createdAt || entry.createdAt < earliest) return false;
      }

      if (q.length) {
        const name = entry.name?.toLowerCase() || '';
        const email = entry.email?.toLowerCase() || '';
        if (!name.includes(q) && !email.includes(q)) return false;
      }
      return true;
    });
  }, [combinedCheckins, optInOnly, range, search]);

  const stats = useMemo(() => {
    const optedIn = combinedCheckins.filter(c => c.optIn).length;
    const recent = combinedCheckins.filter(c => {
      const sevenDays = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return c.createdAt >= sevenDays;
    }).length;
    return { total: combinedCheckins.length, optedIn, recent };
  }, [combinedCheckins]);

  const recipientsCount = filteredCheckins.length;
  const totalPages = Math.max(1, Math.ceil(recipientsCount / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const visibleCheckins = filteredCheckins.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );

  useEffect(() => {
    setPage(prev => Math.min(prev, totalPages - 1));
  }, [totalPages]);

  const handleNextPage = useCallback(async () => {
    const nextPage = currentPage + 1;
    if (nextPage < totalPages) {
      setPage(nextPage);
      return;
    }
    if (hasMore && !loadingPage) {
      const targetIndex = pages.length;
      await loadPage(targetIndex);
      setPage(prev => prev + 1);
    }
  }, [currentPage, totalPages, hasMore, loadingPage, pages.length, loadPage]);

  const handlePrevPage = useCallback(() => {
    setPage(p => Math.max(0, p - 1));
  }, []);

  const value: CheckinsContextValue = {
    loading,
    loadingPage,
    hasMore,
    range,
    setRange,
    optInOnly,
    setOptInOnly,
    search,
    setSearch,
    currentPage,
    totalPages,
    visibleCheckins,
    filteredCheckins,
    recipientsCount,
    stats,
    handleNextPage,
    handlePrevPage,
    refresh,
  };

  return (
    <CheckinsContext.Provider value={value}>
      {children}
    </CheckinsContext.Provider>
  );
};
